import { Modal, Button } from '../../atoms'
import { useShowMessage } from '../../providers'

import { TextAreaField, RadioField } from '../FormFields/FormFields'
import { User } from 'react-feather'
import { Form, Formik, FormikProps } from 'formik'
import { markUnfavourite } from '@pepper/utils/src/api/users/users'

import * as Yup from 'yup'

import s from './UnfavouriteCreatorModal.module.css'

type Props = {
  creator: {
    id: string
    name: string
    role: string
    isFavourite: boolean
  }
  companyId: string
  isOpen: boolean
  onClose: () => void
  onSucess: () => void
}

const reasonOptions = [
  {
    label: 'Favourited by mistake',
    value: '1'
  },
  {
    label: 'Not satisfactory / Decline in quality',
    value: '2'
  },
  {
    label: 'Not suitable for further projects',
    value: '3'
  }
]

const validationSchema: Yup.ObjectSchema = Yup.object({
  unfavourite_reason_id: Yup.string().required('Please specify a reason')
})

export const UnfavouriteCreatorModal = ({
  creator,
  companyId,
  isOpen,
  onClose,
  onSucess
}: Props): JSX.Element => {
  const showMessage = useShowMessage()

  const handleSubmit = async (values: any, formikProps: any) => {
    try {
      const { status } = await markUnfavourite({
        companyId,
        creatorId: creator.id,
        unfavourite_comment: values.unfavourite_comment,
        unfavourite_reason_id: values.unfavourite_reason_id
      })

      if (status === 'ok') {
        showMessage({
          type: 'success',
          message: `${creator.name} has been successfully removed from your favourites list.`
        })
        onSucess()
      }
      formikProps.resetForm()
      onClose()
    } catch (error) {
      showMessage({
        type: 'error',
        message: `Unable remove from favourites. Please try again.`
      })
      return error
    }
  }

  return (
    <>
      <Formik
        initialValues={{}}
        onSubmit={(values, formikProps) => handleSubmit(values, formikProps)}
        validationSchema={validationSchema}
        validateOnBlur={true}
      >
        {(formikProps: FormikProps<any>) => {
          return (
            <Modal
              visible={isOpen}
              className={s.modal}
              onCancel={() => {
                onClose()
                formikProps.resetForm()
              }}
              title="Remove creator from favourites?"
              footer={
                <>
                  <Button
                    type="default"
                    onClick={() => {
                      onClose()
                      formikProps.resetForm()
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    disabled={!formikProps.isValid}
                    htmlType="submit"
                    data-id="submit_unFavouriteBtn"
                    onClick={() => formikProps.submitForm()}
                  >
                    Submit
                  </Button>
                </>
              }
              onOk={formikProps.submitForm}
              cancelText="Cancel"
              closable={true}
            >
              <div className={s.user}>
                <User size={16} />
                <span>{creator.name}</span>
                <span className={s.userRole}>{creator.role}</span>
              </div>

              <Form>
                <RadioField
                  label="Please select reasons for removing this creator:"
                  name="unfavourite_reason_id"
                  fieldProps={{ options: reasonOptions }}
                  className={s.radioGroup}
                  required={true}
                />
                <div className={s.error}>
                  {Object.values(formikProps.errors)}
                </div>
                <TextAreaField
                  name="unfavourite_comment"
                  className={s.textarea}
                  setFieldTouched={formikProps.setFieldTouched}
                  fieldProps={{
                    placeholder: 'Add comments...'
                  }}
                />
              </Form>
            </Modal>
          )
        }}
      </Formik>
    </>
  )
}
